import { FileDownloader } from './helpers/file-downloader';
import { ProgressLog } from './logger';
import { Adb, AdbDaemonTransport, AdbSubprocessProtocol } from '@yume-chan/adb';
import AdbWebCredentialStore from '@yume-chan/adb-credential-web';
import { AdbDaemonWebUsbDevice, AdbDaemonWebUsbDeviceManager } from '@yume-chan/adb-daemon-webusb';
import { PackageManager } from '@yume-chan/android-bin';
import { TextDecoderStream } from '@yume-chan/stream-extra';

export class Device {
  public manager: AdbDaemonWebUsbDeviceManager | undefined = AdbDaemonWebUsbDeviceManager.BROWSER;

  private device: AdbDaemonWebUsbDevice | undefined;
  private credentialStore: AdbWebCredentialStore = new AdbWebCredentialStore();
  private _adb: Adb | undefined;

  constructor() {
    if (!this.manager) {
      console.error('WebUSB is not supported in this browser');
    }
  }

  public get adb(): Adb | undefined {
    return this._adb;
  }

  public async selectDevice(): Promise<boolean> {
    if (!this.manager) {
      return false;
    }
    this.device = await this.manager.requestDevice();
    return this.device !== undefined;
  }

  public async releaseDevice(): Promise<void> {
    if (this.device) {
      await this.device.raw.close();
    }
  }

  public getDeviceName(): string | undefined {
    return this.device?.name;
  }

  public getDeviceId(): string | undefined {
    return this.device?.serial;
  }

  public getDeviceSerialId(): string | undefined {
    return this.device?.raw.serialNumber;
  }

  public getDeviceManufacturerName(): string | undefined {
    return this.device?.raw.manufacturerName;
  }

  public getDeviceVendorId(): number | undefined {
    return this.device?.raw.vendorId;
  }

  public getDeviceVersionMajor(): number | undefined {
    return this.device?.raw.deviceVersionMajor;
  }

  public getDeviceVersionMinor(): number | undefined {
    return this.device?.raw.deviceVersionMinor;
  }

  public getDeviceVersionSubMinor(): number | undefined {
    return this.device?.raw.deviceVersionSubminor;
  }

  public getDeviceProductId(): number | undefined {
    return this.device?.raw.productId;
  }

  public getDeviceProductName(): string | undefined {
    return this.device?.raw.productName;
  }

  public async connect(): Promise<void> {
    if (!this.device) {
      return;
    }
    const connection = await this.device.connect();
    this._adb = new Adb(
      await AdbDaemonTransport.authenticate({
        serial: this.device.serial,
        connection,
        credentialStore: this.credentialStore,
      }),
    );
  }

  public isConnected(): boolean {
    return this.adb !== undefined;
  }

  public async sendCommand(command: string): Promise<{ code: number; logs: string }> {
    if (!this.adb) {
      return Promise.resolve({ code: -1, logs: '' });
    }
    const process: AdbSubprocessProtocol = await this.adb.subprocess.shell(command);
    const stream = process.stdout.pipeThrough(new TextDecoderStream());
    const code = await process.exit;
    const reader = stream.getReader();
    let logs = '';
    let isDone = false;
    while (!isDone) {
      const { value, done } = await reader.read();
      if (value) {
        logs += value;
      }
      isDone = done;
    }
    return Promise.resolve({ code, logs });
  }

  public async reboot(): Promise<void> {
    if (!this.adb || !this.manager || !this.device) {
      return;
    }
    await this.adb.power.reboot();
  }

  public async installApkFromUrl(url: string, progressLog?: ProgressLog): Promise<number> {
    if (!this.adb) {
      return Promise.resolve(-1);
    }

    console.debug(`Installing APK from ${url}`);

    const fileDownloader = new FileDownloader(url);
    const { size, stream } = await fileDownloader.getStream(progressLog);

    const pm = new PackageManager(this.adb);
    await pm.installStream(size, stream);

    return Promise.resolve(0);
  }
}
